import type React from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { useFormatDecimal, useFormatUnit } from "@/formatters"
import { type ResourceName, resourceI18nItemKey } from "@/models/resource"
import { FONT_WEIGHT } from "@/models/typography"
import type { UnitName } from "@/models/unit"
import { GraphMenuContainer } from "@/nzds/graphs/graph-menu/graph-menu-container"
import { GRAPH_PNG_EXCLUDE_ATTR } from "@/nzds/graphs/graph-to-png/graph-to-png"
import { TextField } from "@/nzds/inputs/text-field"
import type { IntervalDisplayUnit } from "@/services/interval"
import type { IntervalHourlyUsageProfileResponse } from "@/services/interval/interval-hourly-usage-profile.types"
import { inclusiveIntervalEnd } from "@/utils/date"
import { getUnitNameForServerUnit } from "@/utils/unit"
import Color from "color"
import type { Interval } from "luxon"

import {
  Box,
  MenuItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { useTheme } from "@mui/material/styles"
import type { SxProps, Theme } from "@mui/material/styles"

import { AverageHourlyUsageProfileCsvMenuItem } from "./average-hourly-usage-profile-csv-menu-item"
import {
  HOUR_COLUMNS,
  getHourlyProfileMatrix,
  getSundayFirstWeekdays,
} from "./hourly-profile-matrix"
import { buildIntervalExportFileName } from "./interval-items-to-csv-table"

const HIGHLIGHT_BORDER_WIDTH_PX = 2
const HIGHLIGHT_SHADOW_VAR = "--profile-highlight-shadow"
const PROFILE_SKELETON_HEIGHT = 360
const PROFILE_TOTAL_COLUMN_WIDTH = "3.5rem"
const PROFILE_CELL_SX = {
  border: "none",
  boxSizing: "border-box",
  fontVariantNumeric: "tabular-nums",
  px: 0.5,
  whiteSpace: "nowrap",
} as const
/** ColorBrewer RdYlGn reversed — Sigma Site Overview Average hourly usage profile heatmap. */
const HEATMAP_STOP_DESATURATION = 0.25
const HEATMAP_STOP_COLORS = [
  "#1A9850",
  "#91CF60",
  "#FFFFBF",
  "#FC8D59",
  "#D73027",
].map((hex) => new Color(hex).desaturate(HEATMAP_STOP_DESATURATION))

const mixHeatmapStops = (normalizedValue: number): string => {
  const lastIndex = HEATMAP_STOP_COLORS.length - 1
  const scaled = normalizedValue * lastIndex
  const startIndex = Math.min(Math.floor(scaled), lastIndex - 1)
  const localT = scaled - startIndex

  return HEATMAP_STOP_COLORS[startIndex]
    .mix(HEATMAP_STOP_COLORS[startIndex + 1], localT)
    .hex()
}

const heatmapBackgroundColor = (
  value: number | null,
  valueRange: { max: number; min: number } | undefined
): string | undefined => {
  if (value == null || valueRange == null) {
    return undefined
  }

  const normalizedValue =
    valueRange.max === valueRange.min
      ? 1
      : (value - valueRange.min) / (valueRange.max - valueRange.min)

  return mixHeatmapStops(normalizedValue)
}

interface HoveredHourCell {
  dayIndex: number
  hour: number
}

interface CellHighlight {
  bottom?: boolean
  insetBottom?: boolean
  insetLeft?: boolean
  insetRight?: boolean
  insetTop?: boolean
  left?: boolean
  right?: boolean
  top?: boolean
}

interface HighlightPosition {
  dayIndex?: number
  hour?: number
  isColumnEnd?: boolean
  isColumnStart?: boolean
  isRowEnd?: boolean
  isRowStart?: boolean
}

const cellHighlight = (
  hoveredCell: HoveredHourCell | null,
  position: HighlightPosition
): CellHighlight | undefined => {
  const isHoveredColumn =
    hoveredCell != null && position.hour === hoveredCell.hour
  const isHoveredRow =
    hoveredCell != null && position.dayIndex === hoveredCell.dayIndex

  if (isHoveredColumn === isHoveredRow) {
    return undefined
  }
  if (isHoveredRow) {
    return {
      bottom: true,
      insetLeft: Boolean(position.isRowStart),
      insetRight: Boolean(position.isRowEnd),
      top: true,
    }
  }

  return {
    insetBottom: Boolean(position.isColumnEnd),
    insetTop: Boolean(position.isColumnStart),
    left: true,
    right: true,
  }
}

const highlightEdgeShadow = (
  color: string,
  x: number,
  y: number,
  inset: boolean
): string => {
  const offset = `${x * HIGHLIGHT_BORDER_WIDTH_PX}px ${y * HIGHLIGHT_BORDER_WIDTH_PX}px 0 0 ${color}`

  return inset ? `inset ${offset}` : offset
}

const outsideEdgeShadows = (
  highlight: CellHighlight,
  color: string
): string[] => {
  const shadows: string[] = []
  if (highlight.bottom === true) {
    shadows.push(highlightEdgeShadow(color, 0, 1, false))
  }
  if (highlight.left === true) {
    shadows.push(highlightEdgeShadow(color, -1, 0, false))
  }
  if (highlight.right === true) {
    shadows.push(highlightEdgeShadow(color, 1, 0, false))
  }
  if (highlight.top === true) {
    shadows.push(highlightEdgeShadow(color, 0, -1, false))
  }

  return shadows
}

const insetCapShadows = (highlight: CellHighlight, color: string): string[] => {
  const shadows: string[] = []
  if (highlight.insetBottom === true) {
    shadows.push(highlightEdgeShadow(color, 0, -1, true))
  }
  if (highlight.insetLeft === true) {
    shadows.push(highlightEdgeShadow(color, 1, 0, true))
  }
  if (highlight.insetRight === true) {
    shadows.push(highlightEdgeShadow(color, -1, 0, true))
  }
  if (highlight.insetTop === true) {
    shadows.push(highlightEdgeShadow(color, 0, 1, true))
  }

  return shadows
}

const highlightBoxShadow = (
  highlight: CellHighlight | undefined,
  color: string
): string | undefined => {
  if (highlight == null) {
    return undefined
  }

  const shadows = [
    ...outsideEdgeShadows(highlight, color),
    ...insetCapShadows(highlight, color),
  ]
  if (shadows.length === 0) {
    return undefined
  }

  return shadows.join(", ")
}

const highlightOverlayAfterSx = (after: React.CSSProperties) => ({
  "&::after": {
    ...after,
    boxSizing: "border-box",
    content: '""',
    inset: 0,
    pointerEvents: "none",
    position: "absolute",
    zIndex: 1,
  },
  overflow: "visible",
  position: "relative",
  zIndex: 1,
})

const highlightOverlaySx = (
  highlight: CellHighlight | undefined,
  color: string
) => {
  const boxShadow = highlightBoxShadow(highlight, color)
  if (boxShadow == null) {
    return {}
  }

  return highlightOverlayAfterSx({ boxShadow })
}

const heatmapTextColor = (
  backgroundColor: string | undefined,
  lightOnDark: string
): string | undefined => {
  if (backgroundColor == null || !new Color(backgroundColor).isDark()) {
    return undefined
  }

  return lightOnDark
}

const cellSurfaceStyle = (
  backgroundColor: string | undefined,
  boxShadow: string | undefined,
  color?: string
): React.CSSProperties | undefined => {
  if (backgroundColor == null && boxShadow == null && color == null) {
    return undefined
  }

  return {
    backgroundColor,
    color,
    [HIGHLIGHT_SHADOW_VAR]: boxShadow ?? "",
  } as React.CSSProperties
}

interface AverageHourlyUsageProfileTableProps {
  dateRange: Interval
  isError: boolean
  isLoading: boolean
  profile: IntervalHourlyUsageProfileResponse | undefined
  resource?: ResourceName
  siteId: string
  unit?: IntervalDisplayUnit
}

const withHourlyProfileUnit = (
  title: string,
  unit?: IntervalDisplayUnit
): string => (unit == null ? title : `${title} (${unit})`)

const getDownloadableHourlyProfileAverages = (
  isError: boolean,
  isLoading: boolean,
  profile: IntervalHourlyUsageProfileResponse | undefined
): (number | null)[][] | undefined => {
  if (isLoading || isError || profile?.state !== "ready") {
    return undefined
  }
  if (getHourlyProfileMatrix(profile.averages).visibleDays.length === 0) {
    return undefined
  }

  return profile.averages
}

type ProfileStatusVariant =
  | "error"
  | "no_contributing_hours"
  | "no_hourly_meters"

interface ProfileStatusCopy {
  description: string
  title: string
}

const PROFILE_STATUS_COPY: Record<ProfileStatusVariant, ProfileStatusCopy> = {
  error: {
    description: "intervalData.hourlyProfile.error.description",
    title: "intervalData.hourlyProfile.error.title",
  },
  no_contributing_hours: {
    description:
      "intervalData.hourlyProfile.empty.noContributingHours.description",
    title: "intervalData.hourlyProfile.empty.noContributingHours.title",
  },
  no_hourly_meters: {
    description: "intervalData.hourlyProfile.empty.noHourlyMeters.description",
    title: "intervalData.hourlyProfile.empty.noHourlyMeters.title",
  },
}

interface ProfileStatusProps {
  resource?: ResourceName
  variant: ProfileStatusVariant
}

const ProfileStatus: React.FC<ProfileStatusProps> = ({ resource, variant }) => {
  const { t } = useTranslation("sites")
  const copy = PROFILE_STATUS_COPY[variant]
  const resourceLabel =
    resource == null
      ? undefined
      : t(`fields.resource.items.${resourceI18nItemKey[resource]}`, {
          ns: "common",
        })

  return (
    <Stack role="status" sx={{ gap: 0.5 }}>
      <Typography variant="body2">{t(copy.title)}</Typography>
      <Typography color="text.secondary" variant="body2">
        {t(copy.description, { resource: resourceLabel })}
      </Typography>
    </Stack>
  )
}

interface ProfileSkeletonProps {
  title: string
}

const ProfileSkeleton: React.FC<ProfileSkeletonProps> = ({ title }) => {
  const { t } = useTranslation("sites")

  return (
    <Skeleton
      aria-label={t("loadingAriaLabel", {
        ns: "common",
        title,
      })}
      data-testid="average-hourly-usage-profile-skeleton"
      height={PROFILE_SKELETON_HEIGHT}
      variant="rounded"
      width="100%"
    />
  )
}

interface ProfileNumericCellProps {
  backgroundColor?: string
  display: string
  fontWeight?: number
  highlight?: CellHighlight
  onMouseEnter?: () => void
}

const ProfileNumericCell: React.FC<ProfileNumericCellProps> = ({
  backgroundColor,
  display,
  fontWeight,
  highlight,
  onMouseEnter,
}) => {
  const theme = useTheme()
  const highlightColor = theme.palette.primary.main

  return (
    <TableCell
      align="right"
      onMouseEnter={onMouseEnter}
      style={cellSurfaceStyle(
        backgroundColor,
        highlightBoxShadow(highlight, highlightColor),
        heatmapTextColor(backgroundColor, theme.palette.common.white)
      )}
      sx={[{ fontWeight }, highlightOverlaySx(highlight, highlightColor)]}
      variant="body"
    >
      {display}
    </TableCell>
  )
}

interface ProfileMatrixProps {
  averages: (number | null)[][]
  unit?: UnitName
  weekStartWeekday: number
}

const ProfileMatrix: React.FC<ProfileMatrixProps> = ({
  averages,
  unit,
  weekStartWeekday,
}) => {
  const { i18n, t } = useTranslation("sites")
  const { formatDecimal } = useFormatDecimal(
    { precision: 1 },
    { placeholder: "", showUnit: false, unit }
  )
  const theme = useTheme()
  const [hoveredCell, setHoveredCell] = useState<HoveredHourCell | null>(null)
  const weekdays = getSundayFirstWeekdays(i18n.language)
  const { columnTotals, grandTotal, valueRange, visibleDays } =
    getHourlyProfileMatrix(averages, weekStartWeekday)
  const highlightColor = theme.palette.primary.main
  const surfaceHighlight = (position: HighlightPosition) => {
    const highlight = cellHighlight(hoveredCell, position)

    return {
      style: cellSurfaceStyle(
        undefined,
        highlightBoxShadow(highlight, highlightColor)
      ),
      sx: highlightOverlaySx(highlight, highlightColor),
    }
  }

  return (
    <TableContainer sx={{ p: `${HIGHLIGHT_BORDER_WIDTH_PX}px` }}>
      <Table
        aria-label={t("intervalData.hourlyProfile.ariaLabel")}
        onMouseLeave={() => {
          setHoveredCell(null)
        }}
        size="small"
        sx={{
          borderCollapse: "separate",
          borderSpacing: 0,
          width: "100%",
          "& .MuiTableCell-root": PROFILE_CELL_SX,
        }}
      >
        <TableHead
          sx={{
            "& .MuiTableCell-root": {
              borderBottom: "none",
              boxShadow: `inset 0 -1px 0 0 ${(theme.vars || theme).palette.divider}`,
            },
          }}
        >
          <TableRow>
            <TableCell align="left" sx={{ fontWeight: FONT_WEIGHT.bold }}>
              {t("intervalData.hourlyProfile.table.hourOfDay")}
            </TableCell>
            {HOUR_COLUMNS.map((hour) => (
              <TableCell
                align="right"
                key={hour}
                {...surfaceHighlight({ hour, isColumnStart: true })}
              >
                {hour}
              </TableCell>
            ))}
            <TableCell
              align="right"
              sx={{
                fontWeight: FONT_WEIGHT.bold,
                width: PROFILE_TOTAL_COLUMN_WIDTH,
              }}
            >
              {t("intervalData.hourlyProfile.table.grandTotal")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody
          sx={{
            "& .MuiTableCell-root": { borderBottom: "none" },
          }}
        >
          {visibleDays.map(({ dayAverages, dayIndex, rowTotal }) => (
            <TableRow key={weekdays[dayIndex]}>
              <TableCell
                component="th"
                scope="row"
                {...surfaceHighlight({ dayIndex, isRowStart: true })}
              >
                {weekdays[dayIndex]}
              </TableCell>
              {HOUR_COLUMNS.map((hour) => (
                <ProfileNumericCell
                  backgroundColor={heatmapBackgroundColor(
                    dayAverages[hour],
                    valueRange
                  )}
                  display={formatDecimal(dayAverages[hour])}
                  highlight={cellHighlight(hoveredCell, { dayIndex, hour })}
                  key={hour}
                  onMouseEnter={() => {
                    setHoveredCell({ dayIndex, hour })
                  }}
                />
              ))}
              <ProfileNumericCell
                display={formatDecimal(rowTotal)}
                fontWeight={FONT_WEIGHT.bold}
                highlight={cellHighlight(hoveredCell, {
                  dayIndex,
                  isRowEnd: true,
                })}
              />
            </TableRow>
          ))}
        </TableBody>
        <TableFooter
          sx={{
            "& .MuiTableCell-root": {
              borderBottom: "none",
              borderTop: "none",
              boxShadow: `inset 0 1px 0 0 ${(theme.vars || theme).palette.divider}`,
            },
          }}
        >
          <TableRow>
            <TableCell
              component="th"
              scope="row"
              sx={{ fontWeight: FONT_WEIGHT.bold }}
              variant="body"
            >
              {t("intervalData.hourlyProfile.table.grandTotal")}
            </TableCell>
            {HOUR_COLUMNS.map((hour) => (
              <ProfileNumericCell
                display={formatDecimal(columnTotals[hour])}
                fontWeight={FONT_WEIGHT.bold}
                highlight={cellHighlight(hoveredCell, {
                  hour,
                  isColumnEnd: true,
                })}
                key={hour}
              />
            ))}
            <ProfileNumericCell
              display={formatDecimal(grandTotal)}
              fontWeight={FONT_WEIGHT.bold}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  )
}

interface ProfileBodyProps extends Omit<
  AverageHourlyUsageProfileTableProps,
  "dateRange" | "isLoading" | "siteId" | "unit"
> {
  averages?: (number | null)[][]
  unit?: UnitName
  weekStartWeekday: number
}

const ProfileBody: React.FC<ProfileBodyProps> = ({
  averages,
  isError,
  profile,
  resource,
  unit,
  weekStartWeekday,
}) => {
  if (isError) {
    return <ProfileStatus variant="error" />
  }

  if (profile == null) {
    return null
  }

  if (profile.state === "ready") {
    return (
      <ProfileMatrix
        averages={averages ?? profile.averages}
        unit={unit}
        weekStartWeekday={weekStartWeekday}
      />
    )
  }

  return <ProfileStatus resource={resource} variant={profile.state} />
}

const WEEK_START_WEEKDAYS = new Set<number>([0, 1, 2, 3, 4, 5, 6])

const isWeekStartWeekday = (value: number): boolean =>
  WEEK_START_WEEKDAYS.has(value)

interface WeekStartsOnSelectProps {
  disabled: boolean
  onWeekStartChange: (weekdayIndex: number) => void
  sx?: SxProps<Theme>
  value: number
  weekdays: string[]
}

const WeekStartsOnSelect: React.FC<WeekStartsOnSelectProps> = ({
  disabled,
  onWeekStartChange,
  sx,
  value,
  weekdays,
}) => {
  const { t } = useTranslation("sites")

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const nextValue = Number(event.target.value)
    if (!isWeekStartWeekday(nextValue)) {
      return
    }
    onWeekStartChange(nextValue)
  }

  return (
    <TextField
      disabled={disabled}
      label={t("intervalData.hourlyProfile.weekStartsOn")}
      onChange={handleChange}
      select
      sx={[{ minWidth: 180 }, sx] as SxProps<Theme>}
      value={String(value)}
      {...{ [GRAPH_PNG_EXCLUDE_ATTR]: "" }}
    >
      {weekdays.map((weekday, weekdayIndex) => (
        <MenuItem key={weekday} value={String(weekdayIndex)}>
          {weekday}
        </MenuItem>
      ))}
    </TextField>
  )
}

export const AverageHourlyUsageProfileTable: React.FC<
  AverageHourlyUsageProfileTableProps
> = ({ dateRange, isError, isLoading, profile, resource, siteId, unit }) => {
  const { i18n, t } = useTranslation("sites")
  const { formatUnit } = useFormatUnit()
  const unitName = getUnitNameForServerUnit(unit)
  const unitLabel = formatUnit(unit)
  const [weekStartWeekday, setWeekStartWeekday] = useState(0)
  const averages = getDownloadableHourlyProfileAverages(
    isError,
    isLoading,
    profile
  )
  const title = withHourlyProfileUnit(
    t("intervalData.hourlyProfile.title"),
    unitLabel || undefined
  )
  const exportFileName = buildIntervalExportFileName(
    title,
    siteId,
    dateRange.start?.toISODate(),
    inclusiveIntervalEnd(dateRange)?.toISODate()
  )

  return (
    <GraphMenuContainer
      ariaLabel={t("actionsMenuAriaLabel", {
        ns: "common",
        title,
      })}
      captureElement
      captureTestId="average-hourly-usage-profile-capture"
      extraItems={
        <AverageHourlyUsageProfileCsvMenuItem
          averages={averages}
          fileName={exportFileName}
          weekStartWeekday={weekStartWeekday}
        />
      }
      fileName={exportFileName}
      isExportDisabled={averages == null}
      title={title}
    >
      {isLoading ? (
        <ProfileSkeleton title={title} />
      ) : (
        <Stack spacing={1} sx={{ minWidth: 0, width: "100%" }}>
          <WeekStartsOnSelect
            disabled={averages == null}
            onWeekStartChange={setWeekStartWeekday}
            sx={{ width: "fit-content" }}
            value={weekStartWeekday}
            weekdays={getSundayFirstWeekdays(i18n.language)}
          />
          <Box data-testid="average-hourly-usage-profile">
            <ProfileBody
              averages={averages}
              isError={isError}
              profile={profile}
              resource={resource}
              unit={unitName}
              weekStartWeekday={weekStartWeekday}
            />
          </Box>
        </Stack>
      )}
    </GraphMenuContainer>
  )
}
